import React, { useEffect, useState, useCallback } from "react";
import {
  ScrollText, Loader2, RefreshCw, Trash2, Gift, Crown,
  KeyRound, RotateCcw, Filter, X,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const ACTION_META = {
  admin_set_plan: { icon: Gift, color: "text-purple-300", bg: "bg-purple-500/15", label_ca: "Pla concedit", label_es: "Plan concedido" },
  admin_clear_plan: { icon: RotateCcw, color: "text-amber-300", bg: "bg-amber-500/15", label_ca: "Pla retirat", label_es: "Plan retirado" },
  admin_set_owner: { icon: Crown, color: "text-amber-300", bg: "bg-amber-500/15", label_ca: "Propietat canviada", label_es: "Propiedad cambiada" },
  admin_delete_company: { icon: Trash2, color: "text-rose-300", bg: "bg-rose-500/15", label_ca: "Empresa eliminada", label_es: "Empresa eliminada" },
  admin_generate_reset_link: { icon: KeyRound, color: "text-blue-300", bg: "bg-blue-500/15", label_ca: "Reset link generat", label_es: "Reset link generado" },
};

function fmt(iso, lang) {
  if (!iso) return "—";
  const t = new Date(iso);
  const diff = (Date.now() - t.getTime()) / 1000;
  const tr = (ca, es) => (lang === "es" ? es : ca);
  if (diff < 60) return tr("ara mateix", "ahora mismo");
  if (diff < 3600) return tr(`fa ${Math.floor(diff / 60)} min`,
                              `hace ${Math.floor(diff / 60)} min`);
  if (diff < 86400) return tr(`fa ${Math.floor(diff / 3600)}h`,
                                `hace ${Math.floor(diff / 3600)}h`);
  return t.toLocaleString(lang === "es" ? "es-ES" : "ca-ES",
                          { day: "2-digit", month: "short", hour: "2-digit",
                            minute: "2-digit" });
}

export default function AdminAuditLog() {
  const { lang } = useI18n();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [availableActions, setAvailableActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const tr = (ca, es) => (lang === "es" ? es : ca);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (actionFilter) params.set("action", actionFilter);
      if (actorFilter.trim()) params.set("actor_email", actorFilter.trim());
      const r = await api.get(`/admin/audit-log?${params.toString()}`);
      setRows(r.data.entries || []);
      setTotal(r.data.total || 0);
      if (r.data.available_actions) {
        setAvailableActions(r.data.available_actions);
      }
    } finally {
      setLoading(false);
    }
  }, [actionFilter, actorFilter]);

  useEffect(() => {
    const t = setTimeout(load, actorFilter ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, actorFilter]);

  const hasFilters = !!(actionFilter || actorFilter.trim());
  const clearFilters = () => {
    setActionFilter("");
    setActorFilter("");
  };

  return (
    <div className="card-surface p-5" data-testid="admin-audit-log">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <ScrollText size={14} className="text-slate-400" />
        <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
          {tr("Registre d'accions super-admin",
              "Registro de acciones super-admin")}
        </h2>
        <span className="text-xs text-slate-500 font-mono">({total})</span>
        <Button
          onClick={load}
          variant="outline"
          size="sm"
          disabled={loading}
          className="ml-auto h-7 bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
          data-testid="admin-audit-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap"
           data-testid="admin-audit-filters">
        <Filter size={12} className="text-slate-500" />
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="h-7 px-2 text-xs rounded-md bg-white/5 border border-white/10 text-slate-300 focus:border-blue-500/50 focus:outline-none"
          data-testid="admin-audit-action-filter">
          <option value="">{tr("Totes les accions", "Todas las acciones")}</option>
          {availableActions.map((a) => {
            const meta = ACTION_META[a];
            const label = meta ? (lang === "es" ? meta.label_es : meta.label_ca)
                               : a;
            return <option key={a} value={a}>{label}</option>;
          })}
        </select>
        <Input
          value={actorFilter}
          onChange={(e) => setActorFilter(e.target.value)}
          placeholder={tr("Filtra per email d'admin…",
                          "Filtra por email de admin…")}
          className="h-7 max-w-xs bg-white/5 border-white/10 text-white text-xs"
          data-testid="admin-audit-actor-filter" />
        {hasFilters && (
          <Button
            onClick={clearFilters}
            variant="outline"
            size="sm"
            className="h-7 px-2 bg-rose-500/10 border-rose-500/30 text-rose-200 hover:bg-rose-500/20"
            data-testid="admin-audit-clear-filters">
            <X size={11} className="mr-1" />
            {tr("Neteja", "Limpiar")}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">
          {hasFilters
            ? tr("Cap acció coincideix amb els filtres",
                 "Ninguna acción coincide con los filtros")
            : tr("Cap acció registrada encara",
                 "Ninguna acción registrada todavía")}
        </p>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r, i) => {
            const meta = ACTION_META[r.action] || {
              icon: ScrollText, color: "text-slate-300",
              bg: "bg-white/5", label_ca: r.action, label_es: r.action,
            };
            const Icon = meta.icon;
            return (
              <div
                key={r.id}
                className="flex items-start gap-3 p-2.5 rounded-lg bg-white/[0.02] border border-white/5 hover:bg-white/[0.04]"
                data-testid={`admin-audit-row-${i}`}>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.bg}`}>
                  <Icon size={12} className={meta.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold ${meta.color}`}>
                      {lang === "es" ? meta.label_es : meta.label_ca}
                    </span>
                    <span className="text-[10px] text-slate-500">·</span>
                    <span className="text-xs text-slate-300 truncate font-mono">
                      {r.actor_email}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                    {r.detail || "—"}
                  </p>
                </div>
                <div className="text-[10px] text-slate-500 font-mono flex-shrink-0 text-right">
                  {fmt(r.timestamp, lang)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
